import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { showLoading } from 'react-global-loading';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL } from '../../../../Constant';
import messages from '../../../../assets/Messages.json';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';

function ParticularsOfFieldOperations() {
  const navigate = useNavigate();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for form data
  const [formData, setFormData] = useState({
    ssoName: '',
    ssoEmployeeCode: '',
    scrutinisingOfficerName: '',
    scrutinisingOfficerCode: '',
    dateReceiptFromFactory: '',
    dateVerificationCompilation: '',
    dateSubmissionBySso: '',
    dateScrutiny: '',
    dateDespatch: '',
    comments: '',
    percentageYield: ''
  });

  // Date picker states
  const [dateReceiptFromFactory, setDateReceiptFromFactory] = useState(null);
  const [dateVerificationCompilation, setDateVerificationCompilation] = useState(null);
  const [dateSubmissionBySso, setDateSubmissionBySso] = useState(null);
  const [dateScrutiny, setDateScrutiny] = useState(null);
  const [dateDespatch, setDateDespatch] = useState(null);

  // Track current month for each date picker
  const [currentMonthReceipt, setCurrentMonthReceipt] = useState(new Date());
  const [currentMonthVerification, setCurrentMonthVerification] = useState(new Date());
  const [currentMonthSubmission, setCurrentMonthSubmission] = useState(new Date());
  const [currentMonthScrutiny, setCurrentMonthScrutiny] = useState(new Date());
  const [currentMonthDespatch, setCurrentMonthDespatch] = useState(new Date());

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  // Refs to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);


  // Authentication configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('jwt'); // Use JWT token first
    
    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') || 
              localStorage.getItem('authToken') || 
              localStorage.getItem('accessToken') ||
              localStorage.getItem('userToken');
    }
    
    console.log('Raw token from localStorage:', token);
    
    if (!token) {
      console.error('No authentication token found');
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
    }
    
    // Clean the token (remove any extra spaces or quotes)
    const cleanToken = token.replace(/['"]/g, '').trim();
    console.log('Cleaned token:', cleanToken);
    
    return {
      headers: {
        'Authorization': `Bearer ${cleanToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  };

  // API call to fetch field operations data
  const fetchFieldOperationsData = async (dslNo, surveyYear) => {
    if (apiCallMadeRef.current || isLoadingRef.current) return;
    
    apiCallMadeRef.current = true;
    isLoadingRef.current = true;
    
    // Show global loading
    showLoading(true);
    
    try {
      const payload = {
        dsl_no: dslNo,
        survey_year: surveyYear.includes('-') ? surveyYear.split('-')[0] : surveyYear // Extract year from format like "2022-23"
      };
      
      console.log('Loading field operations data with payload:', payload);
      const response = await axios.post(`${API_URL}api/fieldOperations/search`, payload, getAuthConfig());
      console.log('Field Operations API Response:', response.data);

      if (response.data.status && response.data.data) {
        const data = response.data.data;


        // Update form data
        setFormData({
          ssoName: data.sso_name || '',
          ssoEmployeeCode: data.sso_employee_code || '',
          scrutinisingOfficerName: data.scrutinising_officer_name || '',
          scrutinisingOfficerCode: data.scrutinising_officer_code || '',
          dateReceiptFromFactory: data.date_receipt_from_factory ? new Date(data.date_receipt_from_factory).toISOString().split('T')[0] : '',
          dateVerificationCompilation: data.date_verification_compilation ? new Date(data.date_verification_compilation).toISOString().split('T')[0] : '',
          dateSubmissionBySso: data.date_submission_by_sso ? new Date(data.date_submission_by_sso).toISOString().split('T')[0] : '',
          dateScrutiny: data.date_scrutiny ? new Date(data.date_scrutiny).toISOString().split('T')[0] : '',
          dateDespatch: data.date_despatch ? new Date(data.date_despatch).toISOString().split('T')[0] : '',
          comments: data.comments || '',
          percentageYield: data.percentage_yield || ''
        });

        // Update date picker states
        const receiptDate = data.date_receipt_from_factory ? new Date(data.date_receipt_from_factory) : null;
        const verificationDate = data.date_verification_compilation ? new Date(data.date_verification_compilation) : null;
        const submissionDate = data.date_submission_by_sso ? new Date(data.date_submission_by_sso) : null;
        const scrutinyDate = data.date_scrutiny ? new Date(data.date_scrutiny) : null;
        const despatchDate = data.date_despatch ? new Date(data.date_despatch) : null;
        
        setDateReceiptFromFactory(receiptDate);
        setDateVerificationCompilation(verificationDate);
        setDateSubmissionBySso(submissionDate);
        setDateScrutiny(scrutinyDate);
        setDateDespatch(despatchDate);
        
        // Update current month states based on loaded dates
        setCurrentMonthReceipt(receiptDate || new Date());
        setCurrentMonthVerification(verificationDate || new Date());
        setCurrentMonthSubmission(submissionDate || new Date());
        setCurrentMonthScrutiny(scrutinyDate || new Date());
        setCurrentMonthDespatch(despatchDate || new Date());

        dataLoadedRef.current = true;

      } else {
        toast.error('No data found for the given parameters', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('Error fetching field operations data:', error);
      toast.error('Error loading field operations data', messages.COLOR_RED);
    } finally {
      isLoadingRef.current = false;
      // Hide global loading
      showLoading(false);
    }
  };

  // Save field operations data to API
  const handleSaveToAPI = async () => {
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    // Check if token exists before making API call
    let token = localStorage.getItem('jwt'); // Use JWT token first
    
    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') || 
              localStorage.getItem('authToken') || 
              localStorage.getItem('accessToken') ||
              localStorage.getItem('userToken');
    }
    
    if (!token) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return false;
    }

    // Show global loading
    showLoading(true);

    try {
      const payload = {
        dsl_no: headerData.despatchSlNo,
        survey_year: headerData.surveyYear.includes('-') ? headerData.surveyYear.split('-')[0] : headerData.surveyYear,
        psl_no: headerData.permanentSlNo,
        company_name: headerData.companyName,
        sso_name: formData.ssoName,
        sso_employee_code: formData.ssoEmployeeCode,
        scrutinising_officer_name: formData.scrutinisingOfficerName,
        scrutinising_officer_code: formData.scrutinisingOfficerCode,
        date_receipt_from_factory: formData.dateReceiptFromFactory,
        date_verification_compilation: formData.dateVerificationCompilation,
        date_submission_by_sso: formData.dateSubmissionBySso,
        date_scrutiny: formData.dateScrutiny,
        date_despatch: formData.dateDespatch,
        comments: formData.comments,
        percentage_yield: formData.percentageYield ? parseFloat(formData.percentageYield) : null
      };

      console.log('Saving field operations data with payload:', payload);
      const response = await axios.post(`${API_URL}api/fieldOperations/create`, payload, getAuthConfig());
      console.log('Create API Response:', response.data);
      
      if (response.data && response.data.status) {
        toast.success('Field operations data saved successfully!', messages.COLOR_RED);
        
        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.FIELD_OPERATIONS);
        // Refresh balance sheet validation to update header colors
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');
        if (balanceSheetData) {
          try {
            const parsedData = JSON.parse(balanceSheetData);
            const dslNo = parsedData.dslNo || parsedData.dsl_no;
            const surveyYear = parsedData.year || parsedData.surveyYear || parsedData.survey_year;
            if (dslNo && surveyYear) {
              await refreshBalanceSheetValidation(dslNo, surveyYear);
            }
          } catch (error) {
            console.error('Error parsing balanceSheetFormData:', error);
          }
        }
        
        return true;
      } else {
        toast.error('Failed to save field operations data', messages.COLOR_RED);
        return false;
      }
    } catch (error) {
      console.error('Error saving field operations data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.', messages.COLOR_RED);
      } else if (error.response?.status === 400) {
        toast.error('Invalid data. Please check your inputs.', messages.COLOR_RED);
      } else {
        toast.error(`Error saving data: ${error.message}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      // Hide global loading
      showLoading(false);
    }
  };

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = () => {
      try {
        // Get form data from localStorage (stored by balanceSheet page)
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);
          const dslNo = parsedData.dslNo || '';
          const surveyYear = parsedData.year || parsedData.surveyYear || new Date().getFullYear().toString();


          setHeaderData({
            despatchSlNo: dslNo,
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: dslNo,
            surveyYear: surveyYear
          });

          // Fetch field operations data if DSL No is available and not already loaded
          if (dslNo && !dataLoadedRef.current && !isLoadingRef.current && !apiCallMadeRef.current) {
            fetchFieldOperationsData(dslNo, surveyYear);
          }
        } else {
          // If no data in localStorage, set defaults
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Filter function to allow only dates from current displayed month (but allow selection to navigate)
  const filterDateByMonth = (date, currentMonth) => {
    if (!currentMonth) return true; // Allow if no current month set
    const dateMonth = date.getMonth();
    const dateYear = date.getFullYear();
    const currentMonthValue = currentMonth.getMonth();
    const currentYear = currentMonth.getFullYear();
    
    // Allow all dates (we'll handle navigation in onSelect)
    // But visually disable dates outside current month via CSS
    return true;
  };

  // Handle month change in date picker
  const handleMonthChange = (date, fieldName) => {
    switch(fieldName) {
      case 'dateReceiptFromFactory':
        setCurrentMonthReceipt(date);
        break;
      case 'dateVerificationCompilation':
        setCurrentMonthVerification(date);
        break;
      case 'dateSubmissionBySso':
        setCurrentMonthSubmission(date);
        break;
      case 'dateScrutiny':
        setCurrentMonthScrutiny(date);
        break;
      case 'dateDespatch':
        setCurrentMonthDespatch(date);
        break;
      default:
        break;
    }
  };

  // Handle date changes
  const handleDateChange = (date, fieldName) => {
    if (!date) return;
    
    // If date is from different month, update the current month state
    const dateMonth = date.getMonth();
    const dateYear = date.getFullYear();
    
    let shouldUpdateMonth = false;
    let currentMonth = null;
    
    switch(fieldName) {
      case 'dateReceiptFromFactory':
        currentMonth = currentMonthReceipt;
        if (currentMonth && (currentMonth.getMonth() !== dateMonth || currentMonth.getFullYear() !== dateYear)) {
          setCurrentMonthReceipt(date);
          shouldUpdateMonth = true;
        }
        setDateReceiptFromFactory(date);
        break;
      case 'dateVerificationCompilation':
        currentMonth = currentMonthVerification;
        if (currentMonth && (currentMonth.getMonth() !== dateMonth || currentMonth.getFullYear() !== dateYear)) {
          setCurrentMonthVerification(date);
          shouldUpdateMonth = true;
        }
        setDateVerificationCompilation(date);
        break;
      case 'dateSubmissionBySso':
        currentMonth = currentMonthSubmission;
        if (currentMonth && (currentMonth.getMonth() !== dateMonth || currentMonth.getFullYear() !== dateYear)) {
          setCurrentMonthSubmission(date);
          shouldUpdateMonth = true;
        }
        setDateSubmissionBySso(date);
        break;
      case 'dateScrutiny':
        currentMonth = currentMonthScrutiny;
        if (currentMonth && (currentMonth.getMonth() !== dateMonth || currentMonth.getFullYear() !== dateYear)) {
          setCurrentMonthScrutiny(date);
          shouldUpdateMonth = true;
        }
        setDateScrutiny(date);
        break;
      case 'dateDespatch':
        currentMonth = currentMonthDespatch;
        if (currentMonth && (currentMonth.getMonth() !== dateMonth || currentMonth.getFullYear() !== dateYear)) {
          setCurrentMonthDespatch(date);
          shouldUpdateMonth = true;
        }
        setDateDespatch(date);
        break;
      default:
        break;
    }
    
    const dateString = date.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      [fieldName]: dateString
    }));
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      ssoName: '',
      ssoEmployeeCode: '',
      scrutinisingOfficerName: '',
      scrutinisingOfficerCode: '',
      dateReceiptFromFactory: '',
      dateVerificationCompilation: '',
      dateSubmissionBySso: '',
      dateScrutiny: '',
      dateDespatch: '',
      comments: '',
      percentageYield: ''
    });
    // Reset date picker states
    setDateReceiptFromFactory(null);
    setDateVerificationCompilation(null);
    setDateSubmissionBySso(null);
    setDateScrutiny(null);
    setDateDespatch(null);
    toast.success('Form reset successfully', messages.COLOR_RED);
  };

  // Handle survey year change and reload data
  const handleSurveyYearChange = (newSurveyYear) => {
    setHeaderData(prev => ({
      ...prev,
      surveyYear: newSurveyYear
    }));
    
    // Reset refs for new data load
    dataLoadedRef.current = false;
    apiCallMadeRef.current = false;
    
    // Reload data with new survey year
    if (headerData.dslNo && newSurveyYear) {
      fetchFieldOperationsData(headerData.dslNo, newSurveyYear);
    }
  };


  return (
    <>
      <ToastContainer />
      <div className="container-fluid py-3">
        {/* Header Section - Direct Display */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-12 mb-3">  
                <h4 className="page_heading mb-3 pb-2 border-bottom">
                  ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
                </h4>
                <h5 className="mb-0 pb-3 border-bottom"><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Particulars of Field Operations</span> </h5>
              </div>
              <div className="col-md-8">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>DSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.despatchSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>PSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.permanentSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>Name of Company:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.companyName}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
       

        {/* Table Styles */}
        <style>
          {`
            .table th {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              font-weight: 600;
              font-size: 14px;
              text-align: center;
              vertical-align: middle;
              padding: 8px 4px;
            }
            .table td {
              border: 1px solid #dee2e6;
              padding: 6px 4px;
              font-size: 13px;
            }
            .table input {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
            }
            .table input:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .table input:disabled {
              background-color: #f8f9fa;
              border-color: #dee2e6;
              color: #6c757d;
              cursor: not-allowed;
            }
            .table select {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
              padding-right: 35px;
            }
            .table select:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .status-field {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              padding: 6px 8px;
              font-size: 12px;
              text-align: center;
              min-width: 120px;
            }
          `}
        </style>

        <style>
          {`
            /* Style for disabled dates (previous/next month dates) */
            .react-datepicker__day--outside-month {
              color: #ccc !important;
              opacity: 0.5 !important;
              cursor: not-allowed !important;
            }
            
            .react-datepicker__day--outside-month:hover {
              background-color: transparent !important;
            }
            
            /* Enable dates from current month */
            .react-datepicker__day:not(.react-datepicker__day--outside-month) {
              color: #000 !important;
              opacity: 1 !important;
            }
            
            /* Print Styles */
            @media print {
              body * {
                visibility: hidden;
              }
              
              .container-fluid, .container-fluid * {
                visibility: visible;
              }
              
              .container-fluid {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
              }
              
              /* Hide buttons and non-essential elements */
              button, .btn, hr {
                display: none !important;
              }
              
              /* Ensure table is visible and formatted */
              table {
                border-collapse: collapse !important;
                width: 100% !important;
              }
              
              table, th, td {
                border: 1px solid #000 !important;
              }
              
              th, td {
                padding: 8px !important;
                font-size: 11px !important;
              }
              
              /* Ensure input values are visible in print */
              input, textarea {
                border: 1px solid #ddd !important;
                background: transparent !important;
                padding: 4px !important;
                font-size: 11px !important;
              }
              
              /* Header styling */
              .page_heading {
                font-size: 16px !important;
                font-weight: bold !important;
              }
              
              /* Remove card backgrounds */
              .card {
                border: none !important;
                box-shadow: none !important;
                background: transparent !important;
              }
              
              /* Ensure all content is visible */
              .table-responsive {
                overflow: visible !important;
              }
              
              /* Label styling */
              .form-label {
                font-weight: bold !important;
                font-size: 11px !important;
              }
              
              /* Characteristic cells */
              .characteristic-cell {
                font-weight: bold !important;
              }
              
              .query-cell, .reason-cell, .code-cell {
                font-size: 10px !important;
              }
            }
          `}
        </style>

        {/* Main Content Area */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
                {/* Left Section - Form */}
                <div className="col-md-6">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name of SSO.:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="ssoName"
                        value={formData.ssoName}
                        onChange={handleInputChange}
                        placeholder="Enter SSO name" 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">SSO Employee Code:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="ssoEmployeeCode"
                        value={formData.ssoEmployeeCode}
                        onChange={handleInputChange}
                        placeholder="Enter employee code" 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name and Designation of Scrutinising officer:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="scrutinisingOfficerName"
                        value={formData.scrutinisingOfficerName}
                        onChange={handleInputChange}
                        placeholder="Enter name and designation" 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Scrutinising officer Code:</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="scrutinisingOfficerCode"
                        value={formData.scrutinisingOfficerCode}
                        onChange={handleInputChange}
                        placeholder="Enter officer code" 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Receipt from Factory:</label>
                      <DatePicker
                        className="form-control calendar_icon"
                        selected={dateReceiptFromFactory}
                        onChange={(date) => handleDateChange(date, 'dateReceiptFromFactory')}
                        onMonthChange={(date) => handleMonthChange(date, 'dateReceiptFromFactory')}
                        month={currentMonthReceipt}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        maxDate={new Date()}
                        onSelect={(date) => {
                          // If date is from different month, navigate to that month
                          if (date && currentMonthReceipt) {
                            const dateMonth = date.getMonth();
                            const dateYear = date.getFullYear();
                            const currentMonthValue = currentMonthReceipt.getMonth();
                            const currentYear = currentMonthReceipt.getFullYear();
                            if (dateMonth !== currentMonthValue || dateYear !== currentYear) {
                              setCurrentMonthReceipt(date);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Verification/ Compilation:</label>
                      <DatePicker
                        className="form-control calendar_icon"
                        selected={dateVerificationCompilation}
                        onChange={(date) => handleDateChange(date, 'dateVerificationCompilation')}
                        onMonthChange={(date) => handleMonthChange(date, 'dateVerificationCompilation')}
                        month={currentMonthVerification}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        maxDate={new Date()}
                        onSelect={(date) => {
                          if (date && currentMonthVerification) {
                            const dateMonth = date.getMonth();
                            const dateYear = date.getFullYear();
                            const currentMonthValue = currentMonthVerification.getMonth();
                            const currentYear = currentMonthVerification.getFullYear();
                            if (dateMonth !== currentMonthValue || dateYear !== currentYear) {
                              setCurrentMonthVerification(date);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Submission by SSO:</label>
                      <DatePicker
                        className="form-control calendar_icon"
                        selected={dateSubmissionBySso}
                        onChange={(date) => handleDateChange(date, 'dateSubmissionBySso')}
                        onMonthChange={(date) => handleMonthChange(date, 'dateSubmissionBySso')}
                        month={currentMonthSubmission}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        maxDate={new Date()}
                        onSelect={(date) => {
                          if (date && currentMonthSubmission) {
                            const dateMonth = date.getMonth();
                            const dateYear = date.getFullYear();
                            const currentMonthValue = currentMonthSubmission.getMonth();
                            const currentYear = currentMonthSubmission.getFullYear();
                            if (dateMonth !== currentMonthValue || dateYear !== currentYear) {
                              setCurrentMonthSubmission(date);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date (s) of Scrutiny:</label>
                      <DatePicker
                        className="form-control calendar_icon"
                        selected={dateScrutiny}
                        onChange={(date) => handleDateChange(date, 'dateScrutiny')}
                        onMonthChange={(date) => handleMonthChange(date, 'dateScrutiny')}
                        month={currentMonthScrutiny}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        maxDate={new Date()}
                        onSelect={(date) => {
                          if (date && currentMonthScrutiny) {
                            const dateMonth = date.getMonth();
                            const dateYear = date.getFullYear();
                            const currentMonthValue = currentMonthScrutiny.getMonth();
                            const currentYear = currentMonthScrutiny.getFullYear();
                            if (dateMonth !== currentMonthValue || dateYear !== currentYear) {
                              setCurrentMonthScrutiny(date);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Despatch:</label>
                      <DatePicker
                        className="form-control calendar_icon"
                        selected={dateDespatch}
                        onChange={(date) => handleDateChange(date, 'dateDespatch')}
                        onMonthChange={(date) => handleMonthChange(date, 'dateDespatch')}
                        month={currentMonthDespatch}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="DD-MM-YYYY"
                        maxDate={new Date()}
                        onSelect={(date) => {
                          if (date && currentMonthDespatch) {
                            const dateMonth = date.getMonth();
                            const dateYear = date.getFullYear();
                            const currentMonthValue = currentMonthDespatch.getMonth();
                            const currentYear = currentMonthDespatch.getFullYear();
                            if (dateMonth !== currentMonthValue || dateYear !== currentYear) {
                              setCurrentMonthDespatch(date);
                            }
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">COMMENTS of Supdt./Sr.Supdt./ Scrutinising officer:</label>
                      <textarea 
                        className="form-control textarea-field" 
                        name="comments"
                        value={formData.comments}
                        onChange={handleInputChange}
                        placeholder="Enter comments here..."
                        rows="4"
                      ></textarea>
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">
                        Percentage yield of product from the basic materials consumed (in case the quantity are common or directly convertible in whole number):
                      </label>
                      <input 
                        type="number" 
                        className="form-control" 
                        name="percentageYield"
                        value={formData.percentageYield}
                        onChange={handleInputChange}
                        placeholder="Enter percentage" 
                        step="0.01"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Section - Reference Table */}
                <div className="col-md-6">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th className="reference-title" colSpan="4">
                          ADD RELEVANT REASON AND CODE FOR ABNORMAL VALUES OF IMPORTANT CHARACTERISTICS IN THE COMMENTS BOX
                        </th>
                      </tr>
                      <tr>
                        <th style={{ width: '20%' }}>Characteristics</th>
                        <th style={{ width: '15%' }}>Query</th>
                        <th style={{ width: '55%' }}>Reason</th>
                        <th style={{ width: '10%' }}>Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="characteristic-cell" rowSpan="10">GVA</td>
                        <td className="query-cell" rowSpan="4">High</td>
                        <td className="reason-cell">1) Increased demand/production</td>
                        <td className="code-cell">1</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Optimal production</td>
                        <td className="code-cell">2</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">3) Profit</td>
                        <td className="code-cell">3</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">4) Govt. subsidy</td>
                        <td className="code-cell">4</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="6">Low</td>
                        <td className="reason-cell">1) Decreased demand/production</td>
                        <td className="code-cell">5</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Non operational</td>
                        <td className="code-cell">6</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">3) Capacity under-utilization</td>
                        <td className="code-cell">7</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">4) Produces input for sister concern</td>
                        <td className="code-cell">8</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">5) High expenses & distributive expenses</td>
                        <td className="code-cell">9</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">6) High input cost</td>
                        <td className="code-cell">10</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="5">Working Capital</td>
                        <td className="query-cell" rowSpan="2">High</td>
                        <td className="reason-cell">1) Profit</td>
                        <td className="code-cell">21</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Diversion of loan/funds to working capital</td>
                        <td className="code-cell">22</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="3">Low</td>
                        <td className="reason-cell">1) Investment in fixed assets</td>
                        <td className="code-cell">23</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Repayment of long term loan</td>
                        <td className="code-cell">24</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">3) Withdrawal of capital</td>
                        <td className="code-cell">25</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="4">Wage Rate</td>
                        <td className="query-cell" rowSpan="2">High</td>
                        <td className="reason-cell">1) Skilled workers</td>
                        <td className="code-cell">31</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Production incentive and overtime etc.</td>
                        <td className="code-cell">32</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="2">Low</td>
                        <td className="reason-cell">1) Cheap rural labor</td>
                        <td className="code-cell">33</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Unskilled workers</td>
                        <td className="code-cell">34</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="3">Workers figure</td>
                        <td className="query-cell" rowSpan="1">High</td>
                        <td className="reason-cell">1) Contract workers included</td>
                        <td className="code-cell">41</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="2">Low</td>
                        <td className="reason-cell">1) Retrenchment/removal</td>
                        <td className="code-cell">42</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Shifted to other units</td>
                        <td className="code-cell">43</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="4">Depreciation</td>
                        <td className="query-cell" rowSpan="2">High</td>
                        <td className="reason-cell">1) Addition to fixed assets</td>
                        <td className="code-cell">51</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) As per income tax records</td>
                        <td className="code-cell">52</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="2">Low</td>
                        <td className="reason-cell">1) Assets shifted to other units</td>
                        <td className="code-cell">53</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Rented unit</td>
                        <td className="code-cell">54</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="3">Distributive expense</td>
                        <td className="query-cell" rowSpan="2">High</td>
                        <td className="reason-cell">1) Increase in trade & advertisement discount</td>
                        <td className="code-cell">61</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Increased tax</td>
                        <td className="code-cell">62</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="1">Low</td>
                        <td className="reason-cell">1) Tax holiday/exemption</td>
                        <td className="code-cell">63</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="3">Ratio of depreciation to output</td>
                        <td className="query-cell" rowSpan="2">High</td>
                        <td className="reason-cell">1) New unit, addition to fixed assets</td>
                        <td className="code-cell">71</td>
                      </tr>
                      <tr>
                        <td className="reason-cell">2) Loss making unit</td>
                        <td className="code-cell">72</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="1">Low</td>
                        <td className="reason-cell">1) Rented facility, no depreciation charged</td>
                        <td className="code-cell">73</td>
                      </tr>
                      <tr>
                        <td className="characteristic-cell" rowSpan="2">Ratio of output to input</td>
                        <td className="query-cell" rowSpan="1">High</td>
                        <td className="reason-cell">1) Servicing unit</td>
                        <td className="code-cell">81</td>
                      </tr>
                      <tr>
                        <td className="query-cell" rowSpan="1">Low</td>
                        <td className="reason-cell">1) High input cost</td>
                        <td className="code-cell">82</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Remarks Section */}
        <div className='row'>
          <Remarks
            remarksData={remarksData}
            setRemarksData={setRemarksData}
            blockItemData={[]}
            filteredBlockItems={[]}
            isLoadingBlockItems={false}
            debouncedBlockItemSearch={() => {}}
            loadMoreBlockItemData={() => {}}
            hasMoreBlockItems={false}
            isLoadingMoreBlockItems={false}
            selectedItemsFromTable={[]}
            remarksList={remarksList}
            availableColumns={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
          />
          <div className='col-md-6'>
                <div className='card'>
                    <div className='card-body'>
                        <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                        <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>                      
                        <li>Average Value per unit in nearest whole rupee is to be reported</li>
                        </ul>
                    </div>
                </div>
            </div>
      
        
    </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/ict-usage-energy-conservation')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/validation')}
            >
              Next 
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        {/* Filter remarks list using centralized function from Custom.js */}
        {(() => {
          const filteredRemarksList = filterRemarksList(remarksList, remarksData);
          return <div className=''>  <Remarks__list remarksData={filteredRemarksList} /> </div>;
        })()}
      </div>
    </>
  );
}

export default ParticularsOfFieldOperations; 